import { AxiosRESTDataSource } from "@kce2/common/api/v2/rest-data-source";
import { ProxyClusterResourceResponseSchema } from "./schema/envoy-gateway";
import { axiosInstance } from "@kce2/app/axios";

const Version = "2023-01-01";
const Action = "ProxyClusterResource";

/** 资源分类 → ApiVersion 映射 */
export const ENVOY_GATEWAY_API_VERSIONS = {
  EnvoyProxy: "gateway.envoyproxy.io/v1alpha1",
  GatewayClass: "gateway.networking.k8s.io/v1",
  Gateway: "gateway.networking.k8s.io/v1",
  HTTPRoute: "gateway.networking.k8s.io/v1",
  GRPCRoute: "gateway.networking.k8s.io/v1",
  Secret: "v1",
  Service: "v1",
} as const;

/** 资源分类 → ResourceType (URL Param) 映射 */
export const ENVOY_GATEWAY_RESOURCE_TYPES = {
  EnvoyProxy: "envoyproxies",
  GatewayClass: "gatewayclasses",
  Gateway: "gateways",
  HTTPRoute: "httproutes",
  GRPCRoute: "grpcroutes",
  Secret: "secrets",
  Service: "services",
} as const;

export type EnvoyGatewayResourceCategory = keyof typeof ENVOY_GATEWAY_RESOURCE_TYPES;

export class EnvoyGatewayApi extends AxiosRESTDataSource {
  baseURL = "/api/cluster";

  /** 查询全量资源列表 (GET) */
  listResources(params: {
    region: string;
    clusterId: string;
    category: EnvoyGatewayResourceCategory;
    namespace?: string;
    fieldSelector?: string;
  }) {
    const { region, clusterId, category, namespace, fieldSelector } = params;
    return this.get("", ProxyClusterResourceResponseSchema, {
      Action,
      Region: region,
      params: {
        Version,
        ClusterId: clusterId,
        ApiVersion: ENVOY_GATEWAY_API_VERSIONS[category],
        ResourceType: ENVOY_GATEWAY_RESOURCE_TYPES[category],
        ...(namespace && { Namespace: namespace }),
        ...(fieldSelector && { fieldSelector }),
      },
    });
  }

  /** 查询单个资源 (GET) */
  getResource(params: {
    region: string;
    clusterId: string;
    category: EnvoyGatewayResourceCategory;
    name: string;
    namespace: string;
    editView?: boolean;
  }) {
    const { region, clusterId, category, name, namespace, editView } = params;
    return this.get("", ProxyClusterResourceResponseSchema, {
      Action,
      Region: region,
      params: {
        Version,
        ClusterId: clusterId,
        ApiVersion: ENVOY_GATEWAY_API_VERSIONS[category],
        ResourceType: ENVOY_GATEWAY_RESOURCE_TYPES[category],
        ResourceName: name,
        Namespace: namespace,
        EditView: editView,
      },
    });
  }

  /** 创建资源 (POST, Content-Type: application/json, body=JSON化的YAML) */
  createResource(params: {
    region: string;
    clusterId: string;
    category: EnvoyGatewayResourceCategory;
    namespace: string;
    body: Record<string, unknown>;
  }) {
    const { region, clusterId, category, namespace, body } = params;
    return this.post("", ProxyClusterResourceResponseSchema, {
      Action,
      Region: region,
      params: {
        Version,
        ClusterId: clusterId,
        ApiVersion: ENVOY_GATEWAY_API_VERSIONS[category],
        ResourceType: ENVOY_GATEWAY_RESOURCE_TYPES[category],
        Namespace: namespace,
      },
      body,
      headers: { "Content-Type": "application/json" },
    });
  }

  /** 更新资源 (PATCH, Content-Type: application/merge-patch+json) */
  updateResource(params: {
    region: string;
    clusterId: string;
    category: EnvoyGatewayResourceCategory;
    name: string;
    namespace: string;
    body: Record<string, unknown>;
  }) {
    const { region, clusterId, category, name, namespace, body } = params;
    return this.patch("", ProxyClusterResourceResponseSchema, {
      Action,
      Region: region,
      params: {
        Version,
        ClusterId: clusterId,
        ApiVersion: ENVOY_GATEWAY_API_VERSIONS[category],
        ResourceType: ENVOY_GATEWAY_RESOURCE_TYPES[category],
        ResourceName: name,
        Namespace: namespace,
      },
      body,
      headers: { "Content-Type": "application/merge-patch+json" },
    });
  }

  /** 删除资源 (DELETE, 不传 body) */
  deleteResource(params: {
    region: string;
    clusterId: string;
    category: EnvoyGatewayResourceCategory;
    name: string;
    namespace: string;
  }) {
    const { region, clusterId, category, name, namespace } = params;
    return this.delete("", ProxyClusterResourceResponseSchema, {
      Action,
      Region: region,
      params: {
        Version,
        ClusterId: clusterId,
        ApiVersion: ENVOY_GATEWAY_API_VERSIONS[category],
        ResourceType: ENVOY_GATEWAY_RESOURCE_TYPES[category],
        ResourceName: name,
        Namespace: namespace,
      },
    });
  }
}

export const envoyGatewayApi = new EnvoyGatewayApi(axiosInstance);
